import { View, Text } from '@react-pdf/renderer';
import { styles } from './PDFStyles';
import { formatCurrency } from '@/utils/currencyFormatter';
import type { CurrencyCode } from '@/utils/currencyFormatter';

interface Package {
  id: number;
  name: string;
  description?: string | null;
  monthly_price?: number | null;
  yearly_price?: number | null;
  package_type?: string | null;
  is_addon?: boolean;
  is_featured?: boolean;
}

interface PDFPackageTableProps {
  packages: Package[];
  providerCurrency: CurrencyCode;
  filterOptions: {
    showPackageDescription?: boolean;
    showPackageMonthlyPrice?: boolean;
    showPackageYearlyPrice?: boolean;
    showPackageType?: boolean;
  };
}

export function PDFPackageTable({ packages, providerCurrency, filterOptions }: PDFPackageTableProps) {
  if (!packages || packages.length === 0) {
    return null;
  }

  console.log('📦 PDFPackageTable: Rendering', packages.length, 'packages');

  // Separate add-ons from regular packages
  const regularPackages = packages.filter(pkg => !pkg.is_addon);
  const addonPackages = packages.filter(pkg => pkg.is_addon);

  const renderPackageSection = (pkgs: Package[], title: string) => {
    if (pkgs.length === 0) return null;

    return (
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>{title}</Text>
        <View style={styles.table}>
          {/* Header Row */}
          <View style={[styles.tableRow, styles.tableHeader]}>
            <View style={[styles.tableCell, { flex: 2 }]}>
              <Text style={styles.headerText}>Package Name</Text>
            </View>
            {filterOptions.showPackageType && (
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text style={styles.headerText}>Type</Text>
              </View>
            )}
            {filterOptions.showPackageMonthlyPrice && (
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text style={styles.headerText}>Monthly</Text>
              </View>
            )}
            {filterOptions.showPackageYearlyPrice && (
              <View style={[styles.tableCell, { flex: 1 }]}>
                <Text style={styles.headerText}>Yearly</Text>
              </View>
            )}
            {filterOptions.showPackageDescription && (
              <View style={[styles.tableCell, { flex: 3 }]}>
                <Text style={styles.headerText}>Description</Text>
              </View>
            )}
          </View>

          {/* Package Rows */}
          {pkgs.map((pkg) => (
            <View key={pkg.id} style={styles.tableRow}>
              <View style={[styles.tableCell, { flex: 2 }]}>
                <View style={{ flexDirection: 'row', alignItems: 'center' }}>
                  <Text style={{ fontWeight: pkg.is_featured ? 700 : 400 }}>
                    {pkg.name}
                    {pkg.is_featured && ' ⭐'}
                  </Text>
                </View>
              </View>
              {filterOptions.showPackageType && (
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text style={{ fontSize: 10, textTransform: 'capitalize' }}>
                    {pkg.package_type?.replace('_', ' ') || '-'}
                  </Text>
                </View>
              )}
              {filterOptions.showPackageMonthlyPrice && (
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>
                    {pkg.monthly_price
                      ? formatCurrency(pkg.monthly_price, providerCurrency)
                      : '-'}
                  </Text>
                </View>
              )}
              {filterOptions.showPackageYearlyPrice && (
                <View style={[styles.tableCell, { flex: 1 }]}>
                  <Text>
                    {pkg.yearly_price
                      ? formatCurrency(pkg.yearly_price, providerCurrency)
                      : '-'}
                  </Text>
                </View>
              )}
              {filterOptions.showPackageDescription && (
                <View style={[styles.tableCell, { flex: 3 }]}>
                  <Text style={{ fontSize: 10, color: '#666' }}>
                    {pkg.description || '-'}
                  </Text>
                </View>
              )}
            </View>
          ))}
        </View>
      </View>
    );
  };

  return (
    <>
      {renderPackageSection(regularPackages, 'Packages')}
      {renderPackageSection(addonPackages, 'Package Add-ons')}
    </>
  );
}
